import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text, Flex } from 'components/ui';
import CompareZoneBase from 'components/Catalog/ProductGrid/CompareModal/Zones/CompareZoneBase';
import bpRedesignedUtils from 'utils/BeautyPreferencesRedesigned';
import plpCompareUtils from 'utils/plpCompare';
import { space } from 'style/config';
import EmptyState from 'components/Catalog/ProductGrid/CompareModal/EmptyState';
import { zoneStyles } from 'components/Catalog/ProductGrid/CompareModal/Zones/zoneStyles';

/**
 * Parse attribute configuration
 * Handles both object format: { labelKey: ['attrKey1', 'attrKey2'] }
 * And string format: 'attrKey'
 */
const parseAttributeConfig = attr => {
    if (typeof attr === 'string') {
        return {
            labelKey: attr,
            attributeKeys: [attr]
        };
    }

    if (typeof attr === 'object' && attr !== null) {
        const keys = Object.keys(attr);

        if (keys.length > 0) {
            const labelKey = keys[0];
            const attributeKeys = attr[labelKey];

            return {
                labelKey,
                attributeKeys: Array.isArray(attributeKeys) ? attributeKeys : [attributeKeys]
            };
        }
    }

    return null;
};

/**
 * Get refinement from masterlistWorld by key
 */
const getRefinement = (masterlistWorld, refinementKey) => {
    if (!masterlistWorld?.refinements) {
        return null;
    }

    return masterlistWorld.refinements.find(ref => ref?.key === refinementKey);
};

/**
 * Map facet values to display names using refinement items
 */
const mapFacetValues = (facetValues, refinement) => {
    if (!refinement?.items) {
        return [];
    }

    return facetValues.map(facet => refinement.items.find(item => item.key === facet)?.value).filter(value => value != null);
};

/**
 * Extracts attribute values from a SKU based on attribute keys
 * Maps facet values to their display names using masterList refinements
 */
const extractAttributeValues = (masterlistWorld, sku, attributeKeys) => {
    if (attributeKeys.length === 0 || !sku?.skuRefinements) {
        return [];
    }

    const allValues = [];

    attributeKeys.forEach(attributeKey => {
        const skuRefinement = sku.skuRefinements.find(ref => ref?.key === attributeKey);

        if (skuRefinement?.values) {
            const masterListRefinement = getRefinement(masterlistWorld, attributeKey);

            if (masterListRefinement) {
                const mappedValues = mapFacetValues(skuRefinement.values, masterListRefinement);
                allValues.push(...mappedValues);
            } else {
                // If no master list refinement found, use raw values
                allValues.push(...skuRefinement.values);
            }
        }
    });

    return allValues;
};

const getProductWorldInfo = product => {
    const parentCategoryId = product?.parentProducts?.[0]?.parentCategory?.ancestorCategories?.[0]?.categoryKey;
    let masterlistWorld;
    let plpCompareAttributes;

    // Bath and Body is not part of master list refinements and will require extensive changes to
    // existing implementations to support, so we are hardcoding it to use skincare master list refinements.

    // Hardcode bathAndBody catId
    if (parentCategoryId === 'cat140014') {
        // Hardcode skinCare catId
        masterlistWorld = bpRedesignedUtils.getMasterListRefinementWorldByCategoryId('cat150006');
        // Hardcode Bath & Body recConfig key
        plpCompareAttributes = plpCompareUtils.getPlpCompareAttributes('bathAndBody');
    } else {
        masterlistWorld = bpRedesignedUtils.getMasterListRefinementWorldByCategoryId(parentCategoryId);
        plpCompareAttributes = plpCompareUtils.getPlpCompareAttributes(masterlistWorld?.key);
    }

    return { masterlistWorld, plpCompareAttributes };
};

const parseProductAttributes = (product, getText) => {
    const { masterlistWorld, plpCompareAttributes } = getProductWorldInfo(product);

    if (!masterlistWorld || !plpCompareAttributes || plpCompareAttributes.length === 0) {
        return [];
    }

    const parsedAttributes = plpCompareAttributes
        .map(attr => {
            const config = parseAttributeConfig(attr);

            if (!config) {
                return null;
            }

            const values = extractAttributeValues(masterlistWorld, product, config.attributeKeys);
            const refinement = getRefinement(masterlistWorld, config.labelKey);
            const label = refinement?.value || getText(config.labelKey);

            // Always include attributes, even if they have no values
            return { config, values, label, isEmpty: values.length === 0 };
        })
        .filter(Boolean);

    return parsedAttributes;
};

const AttributeGrid = ({ allProductAttributes, maxAttributeCount, getText }) => {
    return (
        <Flex
            flexDirection='column'
            gap={4}
        >
            {Array.from({ length: maxAttributeCount }, (_, rowIndex) => (
                <Box
                    key={`attribute-row-${rowIndex}`}
                    css={zoneStyles.productsContainer}
                >
                    {allProductAttributes.map((productAttrs, productIndex) => {
                        const attribute = productAttrs[rowIndex];

                        return (
                            <Box
                                key={`product-${productIndex}-attr-${rowIndex}`}
                                css={zoneStyles.productSlot}
                                role='group'
                                aria-label={getText('productInformation', [productIndex + 1])}
                            >
                                <Box css={zoneStyles.zoneContent}>
                                    {attribute ? (
                                        <Flex flexDirection='column'>
                                            <Text
                                                is='h3'
                                                fontSize='sm'
                                                color='gray'
                                            >
                                                {attribute.label || attribute.config.labelKey}:
                                            </Text>
                                            {attribute.isEmpty ? (
                                                <EmptyState />
                                            ) : (
                                                <Box
                                                    is='ul'
                                                    css={styles.attributeList}
                                                >
                                                    {attribute.values.map((value, valIndex) => (
                                                        <Text
                                                            key={`${attribute.config.labelKey}-${value}-${valIndex}`}
                                                            is='li'
                                                            fontSize='base'
                                                            lineHeight='tight'
                                                            paddingLeft={1}
                                                        >
                                                            {value}
                                                        </Text>
                                                    ))}
                                                </Box>
                                            )}
                                        </Flex>
                                    ) : null}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Flex>
    );
};

const OverviewZone = ({ skus = [], getText = () => {} }) => {
    const allProductAttributes = skus.map(product => {
        if (!product) {
            return [];
        }

        return parseProductAttributes(product, getText);
    });

    const maxAttributeCount = Math.max(...allProductAttributes.map(attrs => attrs.length), 0);

    if (maxAttributeCount === 0) {
        return (
            <CompareZoneBase
                skus={skus}
                getText={getText}
                titleKey='overview'
                renderContent={() => <EmptyState />}
            />
        );
    }

    return (
        <CompareZoneBase
            skus={skus}
            getText={getText}
            titleKey='overview'
            customContent={
                <AttributeGrid
                    allProductAttributes={allProductAttributes}
                    maxAttributeCount={maxAttributeCount}
                    getText={getText}
                />
            }
        />
    );
};

const styles = {
    attributeList: {
        listStyleType: 'disc',
        paddingLeft: `${space[4]}px`,
        marginTop: `${space[1]}px`,
        'li::marker': {
            fontSize: '0.7em'
        }
    }
};

OverviewZone.propTypes = {
    skus: PropTypes.arrayOf(PropTypes.object),
    getText: PropTypes.func
};

export default wrapFunctionalComponent(OverviewZone, 'OverviewZone');
